import type { User } from "@api/auth";
import { useAuthStoreHydrated, useAuthUser } from "@lib/auth";
import { useRouter } from "next/router";
import { ReactNode, useEffect } from "react";
import * as analytics from "@/lib/analytics";
import { FullscreenLoader } from "./fullscreen-loader";

export interface AuthGateProps {
  children: ReactNode | ((props: { user: User }) => ReactNode);
}

export function AuthGate(props: AuthGateProps) {
  const router = useRouter();
  const user = useAuthUser();
  const authHydrated = useAuthStoreHydrated();

  useEffect(() => {
    if (!user) {
      return;
    }
    analytics.identify(user);
  }, [user]);

  useEffect(() => {
    // Don't redirect until persisted credentials are loaded — `user` is
    // briefly null on the first client render even when the session is valid.
    if (!authHydrated) {
      return;
    }

    const from = router.asPath;
    if (!user && !router.pathname.startsWith("/auth")) {
      const fromQuery = router.pathname !== "/" ? { from } : undefined;
      router.replace({
        pathname: "/login",
        query: fromQuery,
      });
    }
  }, [authHydrated, router, user]);

  if (!authHydrated || !user) {
    return <FullscreenLoader />;
  }

  if (typeof props.children === "function") {
    return props.children({ user });
  }

  return props.children;
}
